import React, { useEffect, useState, useCallback } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Goal, GoalTask, Task, GoalType, GoalStatus } from '../../../types';
import { getProjectGoals, createGoal, updateGoal, getGoalTasks, linkTaskToGoal, unlinkTaskFromGoal } from '../../../services/goalService';
import { searchProjectTasks } from '../../../services/searchService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import { BM_TITLE_INPUT } from '../../../components/BoardModal';

interface Props { pca: IPublicClientApplication; projectId: string; }

const TYPE_LABEL: Record<number, string> = { 100000000: 'Task Completion', 100000001: 'Numeric', 100000002: 'Currency', 100000003: 'Yes / No' };
const STATUS_LABEL: Record<number, string> = { 100000000: 'Not Started', 100000001: 'In Progress', 100000002: 'At Risk', 100000003: 'Complete', 100000004: 'Cancelled' };
const STATUS_COLOR: Record<number, string> = { 100000000: 'var(--ink-4)', 100000001: 'var(--brand-light)', 100000002: 'var(--warning)', 100000003: 'var(--success)', 100000004: 'var(--ink-4)' };

function GoalProgress({ goal }: { goal: Goal }) {
  const { dagrun_pm_goaltype: type, dagrun_pm_currentvalue: cur, dagrun_pm_targetvalue: tgt, dagrun_pm_unitlabel: unit, dagrun_pm_status: status } = goal;
  if (type === GoalType.YesNo) {
    return (
      <span className="pill" style={{ background: status === GoalStatus.Complete ? 'var(--success)' : 'var(--surface-2)', color: status === GoalStatus.Complete ? '#fff' : 'var(--ink-3)' }}>
        {status === GoalStatus.Complete ? 'Complete' : 'Not Complete'}
      </span>
    );
  }
  const c = cur ?? 0;
  const t = tgt ?? 0;
  const pct = type === GoalType.TaskCompletion ? c : (t > 0 ? Math.min(100, (c / t) * 100) : 0);
  const label = type === GoalType.TaskCompletion
    ? `${Math.round(pct)}%`
    : type === GoalType.Currency
      ? `${c.toLocaleString()} / ${t.toLocaleString()}`
      : `${c} / ${t}${unit ? ' ' + unit : ''}`;
  return (
    <div style={{ width: 120 }}>
      <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--brand)' }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{label}</div>
    </div>
  );
}

export default function GoalsTab({ pca, projectId }: Props) {
  const [goals, setGoals]           = useState<Goal[]>([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState<Goal | null>(null);
  const [linked, setLinked]         = useState<GoalTask[]>([]);
  const [showNew, setShowNew]       = useState(false);
  const [nTitle, setNTitle]         = useState('');
  const [nType, setNType]           = useState<number>(GoalType.TaskCompletion);
  const [nTarget, setNTarget]       = useState('');
  const [nUnit, setNUnit]           = useState('');
  const [nDue, setNDue]             = useState('');
  const [nOwner, setNOwner]         = useState<Person | null>(null);
  const [linkQuery, setLinkQuery]   = useState('');
  const [linkResults, setLinkResults] = useState<Task[]>([]);

  const reload = useCallback(async () => {
    try { setLoading(true); setGoals(await getProjectGoals(pca, projectId)); }
    finally { setLoading(false); }
  }, [pca, projectId]);

  useEffect(() => { reload(); }, [reload]);

  const openDetail = async (g: Goal) => {
    setSelected(g);
    setLinked(await getGoalTasks(pca, g.dagrun_pm_goalid));
  };

  const handleCreate = async () => {
    if (!nTitle.trim()) return;
    const data: any = {
      dagrun_pm_name: nTitle.trim(),
      dagrun_pm_goaltype: nType,
      dagrun_pm_ownerid: nOwner?.aadObjectId ?? null,
      dagrun_pm_ownername: nOwner?.displayName ?? null,
      dagrun_pm_duedate: nDue || null,
    };
    if (nType === GoalType.Numeric || nType === GoalType.Currency) data.dagrun_pm_targetvalue = nTarget ? parseFloat(nTarget) : null;
    if (nType === GoalType.Numeric) data.dagrun_pm_unitlabel = nUnit || null;
    await createGoal(pca, projectId, data);
    setShowNew(false); setNTitle(''); setNTarget(''); setNUnit(''); setNDue(''); setNOwner(null);
    reload();
  };

  const handleSaveDetail = async () => {
    if (!selected) return;
    await updateGoal(pca, selected.dagrun_pm_goalid, {
      dagrun_pm_name: selected.dagrun_pm_name,
      dagrun_pm_description: selected.dagrun_pm_description ?? null,
      dagrun_pm_status: selected.dagrun_pm_status,
      dagrun_pm_ownerid: selected.dagrun_pm_ownerid ?? null,
      dagrun_pm_ownername: selected.dagrun_pm_ownername,
      dagrun_pm_duedate: selected.dagrun_pm_duedate,
      dagrun_pm_currentvalue: selected.dagrun_pm_currentvalue,
    });
    reload(); setSelected(null);
  };

  const handleMarkComplete = async () => {
    if (!selected) return;
    await updateGoal(pca, selected.dagrun_pm_goalid, { dagrun_pm_status: GoalStatus.Complete });
    setSelected({ ...selected, dagrun_pm_status: GoalStatus.Complete });
    reload();
  };

  const doLinkSearch = async (q: string) => {
    setLinkQuery(q);
    if (q.trim().length < 2) { setLinkResults([]); return; }
    try { setLinkResults(await searchProjectTasks(pca, projectId, q.trim())); } catch { setLinkResults([]); }
  };

  const doLink = async (taskId: string) => {
    if (!selected) return;
    await linkTaskToGoal(pca, selected.dagrun_pm_goalid, taskId);
    setLinked(await getGoalTasks(pca, selected.dagrun_pm_goalid));
    setLinkQuery(''); setLinkResults([]);
  };

  const doUnlink = async (gt: GoalTask) => {
    await unlinkTaskFromGoal(pca, gt.dagrun_pm_goaltaskid);
    setLinked(linked.filter(x => x.dagrun_pm_goaltaskid !== gt.dagrun_pm_goaltaskid));
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3>Goals</h3>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ Add Goal</button>
      </div>

      {loading && <div className="spinner" style={{ margin: '40px auto' }} />}
      {!loading && goals.length === 0 && (
        <div className="card" style={{ padding: 48, textAlign: 'center', fontWeight: 300, fontSize: 15, color: 'var(--ink-3)' }}>
          No goals yet. Click "+ Add Goal" to create one.
        </div>
      )}

      {goals.map(g => (
        <div key={g.dagrun_pm_goalid} className="card" onClick={() => openDetail(g)}
          style={{ marginBottom: 8, padding: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{g.dagrun_pm_name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{TYPE_LABEL[g.dagrun_pm_goaltype]}</div>
          </div>
          <span className="pill" style={{ background: STATUS_COLOR[g.dagrun_pm_status], color: '#fff' }}>{STATUS_LABEL[g.dagrun_pm_status]}</span>
          <GoalProgress goal={g} />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', minWidth: 80, textAlign: 'right' }}>{g.dagrun_pm_ownername ?? '—'}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', minWidth: 70, textAlign: 'right' }}>
            {g.dagrun_pm_duedate ? new Date(g.dagrun_pm_duedate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
          </div>
        </div>
      ))}

      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>New Goal</h3>
            <label>Title</label>
            <input autoFocus value={nTitle} onChange={e => setNTitle(e.target.value)} placeholder="Short, specific goal title" style={BM_TITLE_INPUT} />
            <label style={{ marginTop: 12 }}>Type</label>
            <select value={nType} onChange={e => setNType(Number(e.target.value))}>
              <option value={GoalType.TaskCompletion}>Task Completion (% of linked tasks)</option>
              <option value={GoalType.Numeric}>Numeric</option>
              <option value={GoalType.Currency}>Currency</option>
              <option value={GoalType.YesNo}>Yes / No</option>
            </select>
            {(nType === GoalType.Numeric || nType === GoalType.Currency) && (
              <>
                <label style={{ marginTop: 12 }}>Target Value</label>
                <input type="number" step="0.01" value={nTarget} onChange={e => setNTarget(e.target.value)} />
              </>
            )}
            {nType === GoalType.Numeric && (
              <>
                <label style={{ marginTop: 12 }}>Unit Label</label>
                <input value={nUnit} onChange={e => setNUnit(e.target.value)} placeholder="deals, tickets, ..." />
              </>
            )}
            <label style={{ marginTop: 12 }}>Due Date</label>
            <input type="date" value={nDue} onChange={e => setNDue(e.target.value)} />
            <label style={{ marginTop: 12 }}>Owner</label>
            <PeoplePicker pca={pca} value={nOwner} onChange={setNOwner} placeholder="Select owner…" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <div style={{
          position: 'fixed', top: 0, right: 0, width: 520, height: '100vh',
          background: 'var(--surface)', borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.08)', zIndex: 150, overflowY: 'auto', padding: 28,
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={() => setSelected(null)}
              style={{ width: 32, height: 32, borderRadius: 16, background: 'var(--surface-2)', color: 'var(--ink-3)', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
          </div>
          <input value={selected.dagrun_pm_name} onChange={e => setSelected({ ...selected, dagrun_pm_name: e.target.value })}
            style={{ fontSize: 20, fontWeight: 600, border: 'none', padding: 0, background: 'transparent' }} />

          <div style={{ marginTop: 16 }}><span className="eyebrow">DESCRIPTION</span></div>
          <textarea value={selected.dagrun_pm_description ?? ''} onChange={e => setSelected({ ...selected, dagrun_pm_description: e.target.value })}
            style={{ marginTop: 6, minHeight: 60 }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
            <div>
              <div className="eyebrow">STATUS</div>
              <select value={selected.dagrun_pm_status} onChange={e => setSelected({ ...selected, dagrun_pm_status: Number(e.target.value) })} style={{ marginTop: 6 }}>
                {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>OWNER</div>
              <PeoplePicker
                pca={pca}
                value={selected.dagrun_pm_ownerid
                  ? { aadObjectId: selected.dagrun_pm_ownerid, displayName: selected.dagrun_pm_ownername ?? selected.dagrun_pm_ownerid }
                  : null}
                onChange={p => setSelected({ ...selected, dagrun_pm_ownerid: p?.aadObjectId ?? null, dagrun_pm_ownername: p?.displayName ?? null })}
                placeholder="Select owner…"
              />
            </div>
            <div>
              <div className="eyebrow">DUE DATE</div>
              <input type="date" value={selected.dagrun_pm_duedate?.slice(0, 10) ?? ''} onChange={e => setSelected({ ...selected, dagrun_pm_duedate: e.target.value || null })} style={{ marginTop: 6 }} />
            </div>
            {(selected.dagrun_pm_goaltype === GoalType.Numeric || selected.dagrun_pm_goaltype === GoalType.Currency || selected.dagrun_pm_goaltype === GoalType.TaskCompletion) && (
              <div>
                <div className="eyebrow">CURRENT VALUE</div>
                <input type="number" step="0.01" value={selected.dagrun_pm_currentvalue ?? 0}
                  onChange={e => setSelected({ ...selected, dagrun_pm_currentvalue: Number(e.target.value) })}
                  style={{ marginTop: 6 }} />
              </div>
            )}
          </div>

          {selected.dagrun_pm_goaltype === GoalType.YesNo && selected.dagrun_pm_status !== GoalStatus.Complete && (
            <button className="btn-primary" style={{ marginTop: 16 }} onClick={handleMarkComplete}>Mark Complete</button>
          )}

          <div style={{ marginTop: 24 }}><span className="eyebrow">LINKED TASKS ({linked.length})</span></div>
          {linked.length === 0 && <div style={{ color: 'var(--ink-4)', fontSize: 13, marginTop: 6 }}>No tasks linked yet.</div>}
          {linked.map(gt => (
            <div key={gt.dagrun_pm_goaltaskid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
              <span>{gt['_dagrun_pm_taskid_value@OData.Community.Display.V1.FormattedValue'] ?? gt._dagrun_pm_taskid_value.slice(0, 8)}</span>
              <button className="btn-ghost" onClick={() => doUnlink(gt)}>Unlink</button>
            </div>
          ))}
          <input placeholder="Search tasks to link..." value={linkQuery} onChange={e => doLinkSearch(e.target.value)} style={{ marginTop: 12 }} />
          {linkResults.length > 0 && (
            <div className="card" style={{ maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
              {linkResults.map(t => (
                <div key={t.dagrun_pm_taskid} onClick={() => doLink(t.dagrun_pm_taskid)}
                  style={{ padding: 10, cursor: 'pointer', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                  {t.dagrun_pm_name}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border-light)' }}>
            <button className="btn-secondary" onClick={() => setSelected(null)}>Close</button>
            <button className="btn-primary" onClick={handleSaveDetail}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
